import React from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
} from "@mui/material";
import { ReactComponent as Like } from '../../../../assets/images/like.svg';
import { ReactComponent as UnLike } from '../../../../assets/images/unLike.svg';
import NoDataFound from "../../../dashboardcomponents/Traffic/NoDataFound";

const ArticlePerformanceTable = ({
  articles = [],
  rowsPerPage = 5,
  page = 0,
  onPageChange,
  totalArticles = null,
  loading = false,
}) => {
  const handleChangePage = (newPage) => {
    if (onPageChange) onPageChange(newPage);
  };

  const isEmpty = !loading && articles.length === 0;

  // The endpoint does not always report a total. When it does not, a full page
  // is the only signal that another one may exist.
  const parsedTotal = Number(totalArticles);
  const hasTotal = totalArticles != null && Number.isFinite(parsedTotal);
  const firstOnPage = page * rowsPerPage + 1;
  const lastOnPage = hasTotal
    ? Math.min((page + 1) * rowsPerPage, parsedTotal)
    : page * rowsPerPage + articles.length;
  const hasNextPage = hasTotal
    ? (page + 1) * rowsPerPage < parsedTotal
    : articles.length >= rowsPerPage;

  const rangeLabel = isEmpty
    ? "Articles 0–0 of 0"
    : hasTotal
      ? `Articles ${firstOnPage}–${lastOnPage} of ${parsedTotal}`
      : `Articles ${firstOnPage}–${lastOnPage}`;

  // Hide pagination when there are 5 or fewer records (nothing to page through).
  const showPagination =
    !isEmpty &&
    (page > 0 ||
      (hasTotal ? parsedTotal > rowsPerPage : articles.length > rowsPerPage));

  // ────────────────────────────────────────────────
  // Loading Skeleton
  // ────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          marginTop: '25px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 791px)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            color: '#061522',
            fontFamily: 'Inter',
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '18px'
          }}
        >
          Article Performance
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            flex: 1,
            overflow: 'auto',
            minHeight: '400px',
          }}
        >
          <Table sx={{ width: '100%' }}>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <TableRow
                sx={{
                  '& th': {
                    color: '#405261',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    paddingLeft: '0px',
                    backgroundColor: 'background.paper'
                  },
                }}
              >
                <TableCell sx={{ width: '70%' }}>
                  <Skeleton width="60%" height={20} />
                </TableCell>
                <TableCell><Skeleton width="40%" height={20} /></TableCell>
                <TableCell><Skeleton width="40%" height={20} /></TableCell>
                <TableCell align="right" sx={{ paddingLeft: '16px !important', paddingRight: '16px !important' }}>
                  <Box sx={{ display: 'inline-block', minWidth: '60px', textAlign: 'left' }}>
                    <Skeleton width="30px" height={20} />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {[...Array(rowsPerPage)].map((_, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& td": {
                      height: "4rem",
                      py: 0,
                      paddingLeft: 0,
                      verticalAlign: "middle",
                    },
                  }}
                >
                  <TableCell sx={{ width: '800px !important' }}>
                    <Skeleton width="85%" height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="60%" height={24} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton width={30} height={20} />
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton width={30} height={20} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ paddingLeft: '16px !important', paddingRight: '16px !important' }}>
                    <Box sx={{ display: 'inline-block', minWidth: '60px', textAlign: 'left' }}>
                      <Skeleton width="40px" height={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 'auto',
            padding: "16px 24px",
            backgroundColor: '#FFFFFF',
            borderTop: "1px solid #E5E9EC",
          }}
        >
          <Skeleton width="180px" height={20} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Skeleton variant="rectangular" width={128} height={40} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" width={88} height={40} sx={{ borderRadius: '8px' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  // ────────────────────────────────────────────────
  // Main render (header always shown)
  // ────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        marginTop: '25px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 791px)',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color: '#061522',
          fontFamily: 'Inter',
          fontSize: '18px',
          fontWeight: 500,
          lineHeight: '18px'
        }}
      >
        Article Performance
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          flex: 1,
          overflow: 'auto',
          minHeight: '300px',
        }}
      >
        <Table sx={{ width: '100%' }}>
          {/* Header - always visible */}
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,

            }}
          >
            <TableRow
              sx={{
                '& th': {
                  color: '#405261',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 400,
                  paddingTop: '0px',
                  paddingBottom: '9px !important', // Added !important if needed
                  paddingLeft: '0px',

                },
              }}
            >
              <TableCell sx={{ width: '70%' }}>Articles</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell align="right" sx={{ padding: '0px !important' }}>
                <Box sx={{ display: 'inline-block', minWidth: '56px', textAlign: 'left' }}>
                  Score
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ height: '400px', p: 0 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',

                    }}
                  >
                    <NoDataFound
                      containerStyle={{ height: 'auto' }}

                      title="No articles found"
                      isForAnalytics={true}
                      showBackButton={false}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              articles
                //
                .map((item, index) => {
                  const good = item.good || 0;
                  const bad = item.bad || 0;
                  const score = item.score ?? '—';

                  return (
                    <TableRow
                      key={item._id || index}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#F7F8F9 !important",
                        },
                        "& td": {
                          height: "4rem",
                          py: 0,
                          paddingLeft: 0,
                          verticalAlign: "middle",
                          color: "#405261",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: '70%',
                          fontWeight: 500,
                          color: '#061522 !important',
                        }}
                      >
                        {item.title || 'Untitled'}
                      </TableCell>
                      <TableCell>
                        {(item.views || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '26px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', }}>
                            <Like />
                            <span>{good}</span>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', }}>
                            <UnLike />
                            <span>{bad}</span>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ padding: '0px !important' }}>
                        <Box sx={{ display: 'inline-block', minWidth: '56px', textAlign: 'left' }}>
                          {score}%
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination — hidden when ≤ rowsPerPage records */}
      {showPagination && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            padding: "16px 0px",
            backgroundColor: '#FFFFFF',
            borderTop: "1px solid #E5E9EC",
          }}
        >
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "18px",
            }}
          >
            {rangeLabel}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0 || isEmpty}
              sx={{
                width: "128px",
                height: "40px",
                borderRadius: "8px",
                border: "1px solid #D5DCE1",
                background: "#FFF",
                boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                color: "#405261",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "18px",
                textTransform: "none",
                "&:not(.Mui-disabled):hover": {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#C5CED6",
                },
                "&.Mui-disabled": {
                  color: "#93A1B0",
                  border: "1px solid #C5CED6",
                  bgcolor: "#F9FAFA",
                },
              }}
            >
              Previous
            </Button>

            <Button
              onClick={() => handleChangePage(page + 1)}
              disabled={isEmpty || !hasNextPage}
              sx={{
                width: "88px",
                height: "40px",
                borderRadius: "8px",
                border: "1px solid #D5DCE1",
                boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                color: "#405261",
                backgroundColor: "#FFFFFF",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "18px",
                textTransform: "none",
                "&:not(.Mui-disabled):hover": {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#C5CED6",
                },
                "&.Mui-disabled": {
                  color: "#93A1B0",
                  border: "1px solid #C5CED6",
                  bgcolor: "#F9FAFA",
                },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>)}
    </Box>
  );
};

export default ArticlePerformanceTable;